import { EnrollmentWaitlistSummary } from "./-components/enrollment-waitlist-summary";
import { EnrollmentTotals } from "./-components/enrollment-totals";
import { EnrollmentResultDialog } from "./-components/enrollment-result-dialog";
import { EnrollmentChangesSummary } from "./-components/enrollment-changes-summary";
import { EnrollmentClassCard } from "./-components/enrollment-class-card";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { Clock, Info, AlertCircle, CreditCard, CheckIcon, Loader2Icon } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  useParentEnrollmentCalculateFamilyFees,
  useParentEnrollmentEnrollStudent,
  useParentEnrollmentGetEnrollmentDetails,
} from "@/orval/parent-enrollment";
import { format, parse, parseISO } from "date-fns";
import { EnrollmentStatus, type ParentEnrollmentResultDTO } from "@/orval/model";
import { convertGradeLevelToString } from "@/lib/string-utiils";
import { IconButton } from "@/components/ui/icon-button";
import { formatCurrency } from "@/lib/currency-utils";
import { getTimeRangeString, hasTimeOverlap } from "@/lib/date-utils";
import { parseEnrollmentDetailParams } from "@/lib/route-params";
import { enrollmentSearchSchema, useEnrollmentSemester } from "./-components/use-enrollment-semester";
import { useParentEnrollmentGetEnrollmentStudents } from "@/orval/parent-enrollment";
import { Skeleton } from "@/components/ui/skeleton";
import { PortalPageHeader } from "@/components/portal-page-header";

function EnrollmentDetailRoute() {
  const { organizationId, studentId, classDayId } = Route.useParams();
  const search = Route.useSearch();
  const { semesterId, selectedSemesterId, isPending } = useEnrollmentSemester(organizationId, search.semesterId);
  const students = useParentEnrollmentGetEnrollmentStudents(organizationId, semesterId!, {
    query: { enabled: !!semesterId },
  });
  const navigate = Route.useNavigate();
  const hasClassDay = students.data?.some(
    (student) => student.studentId === studentId && student.classDays.some((day) => day.classDayId === classDayId),
  );
  const isLoading = isPending || (!!semesterId && students.isPending);
  const shouldReturn = !isLoading && (!semesterId || !hasClassDay);
  useEffect(() => {
    if (shouldReturn) {
      void navigate({
        to: "/organizations/$organizationId/parent/enrollment",
        search: { semesterId: selectedSemesterId },
        replace: true,
      });
    }
  }, [shouldReturn, navigate, selectedSemesterId]);
  if (isLoading || shouldReturn) {
    return <Skeleton className="h-64 m-6" />;
  }
  return (
    <EnrollmentDetailPage
      key={`${organizationId}-${studentId}-${classDayId}-${semesterId}`}
      selectedSemesterId={selectedSemesterId}
    />
  );
}

function EnrollmentDetailPage({ selectedSemesterId }: { selectedSemesterId?: number }) {
  const { studentId, classDayId, organizationId } = Route.useParams();
  const navigate = useNavigate();
  const [didSetSelectedClasses, setDidSetSelectedClasses] = useState(false);
  const [enrollmentResult, setEnrollmentResult] = useState<ParentEnrollmentResultDTO | null>(null);
  const { data: enrollmentDetails, isFetching: isLoadingEnrollmentDetails } = useParentEnrollmentGetEnrollmentDetails(
    organizationId,
    studentId,
    classDayId,
  );
  const { mutateAsync: enrollStudent, isPending: isEnrolling } = useParentEnrollmentEnrollStudent();

  const isModifying =
    !!enrollmentDetails && enrollmentDetails.classOptions.some((option) => option.enrollmentStatus !== null);
  const availableClasses = enrollmentDetails?.classOptions || [];
  const previouslySelectedClasses =
    enrollmentDetails?.classOptions.flatMap((option) => (option.enrollmentStatus !== null ? [option.classId] : [])) ||
    [];
  const dayLabel = enrollmentDetails?.classDayName || "Class Day";

  // State for selected classes
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);

  const selectedWaitlistedClasses = useMemo(() => {
    return availableClasses.flatMap((availableClass) => {
      const isNotEligibleForContinuation =
        availableClass.isContinuationClass && !availableClass.isEligibleForContinuation;
      const isWaitlisted =
        availableClass.enrollmentStatus === EnrollmentStatus.Waitlisted ||
        (availableClass.isFull && availableClass.enrollmentStatus === null) ||
        (isNotEligibleForContinuation && availableClass.enrollmentStatus === null);
      return selectedClasses.includes(availableClass.classId) && isWaitlisted ? [availableClass.classId] : [];
    });
  }, [selectedClasses, availableClasses]);

  const waitListedClasses = useMemo(() => {
    return availableClasses.flatMap((availableClass) => {
      const isNotEligibleForContinuation =
        availableClass.isContinuationClass && !availableClass.isEligibleForContinuation;
      const isWaitlisted =
        availableClass.enrollmentStatus === EnrollmentStatus.Waitlisted ||
        (availableClass.isFull && availableClass.enrollmentStatus === null) ||
        (isNotEligibleForContinuation && availableClass.enrollmentStatus === null);
      return isWaitlisted ? [availableClass.classId] : [];
    });
  }, [availableClasses]);

  useEffect(() => {
    if (
      isModifying &&
      selectedClasses.length === 0 &&
      previouslySelectedClasses.length > 0 &&
      !didSetSelectedClasses &&
      !isLoadingEnrollmentDetails
    ) {
      setDidSetSelectedClasses(true);
      setSelectedClasses([...previouslySelectedClasses]);
    }
  }, [isModifying, selectedClasses, previouslySelectedClasses]);

  // Track added and removed classes
  const addedClasses = selectedClasses.filter(
    (id) => !previouslySelectedClasses.includes(id) && !selectedWaitlistedClasses.includes(id),
  );
  const removedClasses = previouslySelectedClasses.filter((id) => !selectedClasses.includes(id));
  const unchangedClasses = selectedClasses.filter(
    (id) => previouslySelectedClasses.includes(id) && !selectedWaitlistedClasses.includes(id),
  );
  const nonWaitlistedClasses = selectedClasses.filter((id) => !selectedWaitlistedClasses.includes(id));

  const { data: familyFees } = useParentEnrollmentCalculateFamilyFees(organizationId, studentId, classDayId, {
    "classIds[]": nonWaitlistedClasses,
  });

  const sortedClasses = [...availableClasses].sort(
    (a, b) =>
      parse(a.startTime, "HH:mm:ss", new Date(0)).getTime() - parse(b.startTime, "HH:mm:ss", new Date(0)).getTime(),
  );

  // Get conflicting class names
  const getConflictingClasses = (classId: number): string[] => {
    const classToCheck = availableClasses.find((c) => c.classId === classId);
    if (!classToCheck) {
      return [];
    }

    return selectedClasses.flatMap((selectedId) => {
      if (selectedId === classId) {
        return [];
      }

      const selectedClass = availableClasses.find((c) => c.classId === selectedId);
      if (!selectedClass) {
        return [];
      }

      if (
        !hasTimeOverlap(classToCheck.startTime, classToCheck.endTime, selectedClass.startTime, selectedClass.endTime) ||
        !selectedClass.className
      ) {
        return [];
      }

      return [selectedClass.className];
    });
  };

  // Toggle class selection
  const toggleClassSelection = (classId: number) => {
    if (selectedClasses.includes(classId)) {
      // Remove class
      setSelectedClasses(selectedClasses.filter((id) => id !== classId));
    } else {
      // Add class
      setSelectedClasses([...selectedClasses, classId]);
    }
  };

  // Get class details by ID
  const getClassDetails = (classId: number) => {
    return availableClasses.find((c) => c.classId === classId);
  };

  // Get selected classes details
  const selectedClassesDetails = useMemo(() => {
    return selectedClasses.flatMap((id) => {
      const classItem = availableClasses.find((c) => c.classId === id);
      return classItem && !selectedWaitlistedClasses.includes(classItem.classId || 0) ? [classItem] : [];
    });
  }, [selectedClasses, selectedWaitlistedClasses, availableClasses]);

  // Add a function to get waitlisted classes details
  const waitlistedClassesDetails = useMemo(() => {
    return selectedWaitlistedClasses.flatMap((id) => {
      const classItem = availableClasses.find((c) => c.classId === id);
      return classItem ? [classItem] : [];
    });
  }, [selectedWaitlistedClasses, availableClasses]);

  // Calculate total class tuition for current selection
  const calculateCurrentTuition = () => {
    return selectedClassesDetails.reduce((total, classItem) => total + (classItem?.price || 0), 0);
  };

  // Calculate total class tuition for previous selection
  const calculatePreviousTuition = () => {
    return previouslySelectedClasses.reduce((total, id) => {
      if (waitListedClasses.includes(id)) {
        return total;
      }
      const classItem = getClassDetails(id);
      return total + (classItem?.price || 0);
    }, 0);
  };

  // Calculate tuition difference
  const calculateTuitionDifference = () => {
    return calculateCurrentTuition() - calculatePreviousTuition();
  };

  // Calculate grand total (class tuition + family fees)
  const calculateGrandTotal = () => {
    return calculateCurrentTuition() + Math.max(familyFees?.difference ?? 0, 0);
  };

  // Calculate grand total difference
  const calculateGrandTotalDifference = () => {
    if (!isModifying) {
      return calculateGrandTotal();
    }
    return calculateTuitionDifference() + (familyFees?.difference ?? 0);
  };

  const hasChanges = useMemo(() => {
    return selectedClasses.sort().toString() !== previouslySelectedClasses.sort().toString();
  }, [selectedClasses, previouslySelectedClasses]);

  const saveButtonText = useMemo(() => {
    if (isLoadingEnrollmentDetails) {
      return "Loading...";
    }

    if (!enrollmentDetails?.canEnroll && enrollmentDetails?.enrollmentStartDateTime) {
      return `Opens ${format(parseISO(enrollmentDetails.enrollmentStartDateTime), "MMM do 'at' h:mm a")}`;
    }

    if (isEnrolling) {
      return "Saving...";
    }

    if (isModifying) {
      return hasChanges ? "Save Changes" : "No Changes to Save";
    }

    return "Complete Enrollment";
  }, [isLoadingEnrollmentDetails, enrollmentDetails, isEnrolling, isModifying, hasChanges]);

  const saveButtonIcon = useMemo(() => {
    if (isEnrolling) {
      return <Loader2Icon className="animate-spin" />;
    }

    if (!enrollmentDetails?.canEnroll) {
      return enrollmentDetails?.enrollmentStartDateTime ? (
        <Clock className="size-4" />
      ) : (
        <AlertCircle className="size-4" />
      );
    }

    return <CheckIcon />;
  }, [isEnrolling, enrollmentDetails]);

  const handleSubmit = async () => {
    const result = await enrollStudent({
      organizationId,
      data: {
        classDayId,
        classIds: selectedClasses,
        studentId,
      },
    });

    // Show enrollment results dialog
    setEnrollmentResult(result);
  };

  const handleContinueEditing = () => {
    setEnrollmentResult(null);
  };

  const handleGoToEnrollments = () => {
    navigate({
      from: Route.fullPath,
      to: "/organizations/$organizationId/parent/enrollment",
      search: { semesterId: selectedSemesterId },
    });
  };

  const enrollmentTitle = `${isModifying ? "Modify" : "New"} ${dayLabel} Enrollment`;
  const selectionAction = isModifying ? "Update" : "Select";
  const enrollmentSubtitle = enrollmentDetails
    ? `${selectionAction} classes for ${enrollmentDetails.studentName} (${convertGradeLevelToString(enrollmentDetails.gradeLevel)})`
    : "Loading enrollment details...";

  return (
    <div className="p-4 md:p-6">
      <PortalPageHeader
        className="mb-6"
        breadcrumbs={[
          {
            label: "Enrollment",
            to: "/organizations/$organizationId/parent/enrollment",
            params: { organizationId },
            search: { semesterId: selectedSemesterId },
          },
          { label: enrollmentDetails?.studentName ?? "Enrollment Details" },
        ]}
        title={enrollmentTitle}
        subtitle={enrollmentSubtitle}
      />

      <Alert className="mb-6">
        <Info className="size-4" />
        <AlertTitle>Class Selection</AlertTitle>
        <AlertDescription>
          {isModifying
            ? "Update your class selections. Previously enrolled classes are pre-selected."
            : "Select classes for enrollment. Classes with overlapping times cannot be selected together."}{" "}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <Card className="bg-transparent">
            <CardHeader>
              <CardTitle>Available Classes</CardTitle>
              <CardDescription>
                Classes are sorted by start time. Click on a class to select or deselect it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-y-4">
                {sortedClasses.map((classItem) => (
                  <EnrollmentClassCard
                    key={classItem.classId}
                    classItem={classItem}
                    selectedClasses={selectedClasses}
                    previouslySelectedClasses={previouslySelectedClasses}
                    addedClasses={addedClasses}
                    removedClasses={removedClasses}
                    conflictingClasses={getConflictingClasses(classItem.classId)}
                    isEnrolling={isEnrolling}
                    isLoadingEnrollmentDetails={isLoadingEnrollmentDetails}
                    isModifying={isModifying}
                    selectedWaitlistedClasses={selectedWaitlistedClasses}
                    toggleClassSelection={toggleClassSelection}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sticky Enrollment Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CreditCard className="mr-2 size-5" />
                  Enrollment Summary
                </CardTitle>
                <CardDescription>
                  {isModifying
                    ? "Review changes to your enrollment and updated costs"
                    : "Review your selected classes and total cost"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedClasses.length === 0 && !isModifying ? (
                  <div className="text-center py-6 text-muted-foreground">No classes selected yet</div>
                ) : (
                  <div className="flex flex-col gap-y-4">
                    {isModifying ? (
                      <EnrollmentChangesSummary
                        addedClasses={addedClasses}
                        removedClasses={removedClasses}
                        unchangedClasses={unchangedClasses}
                        waitListedClasses={waitListedClasses}
                        selectedWaitlistedClasses={selectedWaitlistedClasses}
                        waitlistedClassesDetails={waitlistedClassesDetails}
                        getClassDetails={getClassDetails}
                        tuitionDifference={calculateTuitionDifference()}
                      />
                    ) : (
                      <div>
                        <h3 className="font-medium mb-2">Class Tuition</h3>
                        {selectedClassesDetails.length > 0 ? (
                          <>
                            {selectedClassesDetails.map((classItem) => (
                              <div key={classItem?.classId} className="flex justify-between items-center py-2 border-b">
                                <div>
                                  <div className="font-medium">{classItem?.className}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {getTimeRangeString(classItem?.startTime, classItem?.endTime)}
                                  </div>
                                </div>
                                <div className="font-medium">{formatCurrency(classItem?.price || 0)}</div>
                              </div>
                            ))}

                            <div className="flex justify-between items-center pt-2">
                              <div className="font-medium">Class Tuition Subtotal</div>
                              <div className="font-medium">{formatCurrency(calculateCurrentTuition())}</div>
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-muted-foreground py-2">No classes selected yet</div>
                        )}

                        <EnrollmentWaitlistSummary
                          selectedWaitlistedClasses={selectedWaitlistedClasses}
                          waitlistedClassesDetails={waitlistedClassesDetails}
                        />
                      </div>
                    )}

                    <Separator />

                    <EnrollmentTotals
                      familyFees={familyFees}
                      isModifying={isModifying}
                      previousTuition={calculatePreviousTuition()}
                      grandTotal={calculateGrandTotal()}
                      grandTotalDifference={calculateGrandTotalDifference()}
                    />
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <IconButton
                  icon={saveButtonIcon}
                  className="w-full"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={
                    (selectedClasses.length === 0 && !isModifying) ||
                    (isModifying && !hasChanges) ||
                    isEnrolling ||
                    !enrollmentDetails?.canEnroll
                  }
                >
                  {saveButtonText}
                </IconButton>
                {/* <p className="text-xs text-muted-foreground text-center">
                  {isModifying
                    ? "By saving changes, you agree to pay any additional fees or accept any applicable refunds."
                    : "By completing enrollment, you agree to pay the total amount shown above."}{" "}
                </p> */}
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>

      {/* Enrollment Result Dialog */}
      <EnrollmentResultDialog
        enrollmentResult={enrollmentResult}
        onClose={handleContinueEditing}
        onGoToEnrollments={handleGoToEnrollments}
      />
    </div>
  );
}

export const Route = createFileRoute("/_app/organizations/$organizationId/parent/enrollment_/$studentId/$classDayId")({
  params: {
    parse: parseEnrollmentDetailParams,
  },
  validateSearch: enrollmentSearchSchema,
  component: EnrollmentDetailRoute,
});
